import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import TeamMemberCompensationDialog from '../../components/team-members/TeamMemberCompensationDialog';
import EmployeePayrollAppointmentDetailsDialog from '../../components/employee-payments/EmployeePayrollAppointmentDetailsDialog';
import MembershipMonthlyPotPanel from '../../components/employee-payments/MembershipMonthlyPotPanel';
import MembershipPeriodUsagePanel from '../../components/employee-payments/MembershipPeriodUsagePanel';
import SalovareDateRangePicker from '../../components/common/SalovareDateRangePicker';
import SalovareLoader from '../../components/common/SalovareLoader';
import SalovareSelectField from '../../components/common/SalovareSelectField';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import { FIELD_PLACEHOLDER_COLOR } from '../../components/common/formFieldStyles';
import employeePaymentService from '../../services/employeePaymentService';
import { getPayrollSourceTypeKey, isAppointmentAdditionalCostSource } from '../../utils/payrollSourceTypeUtils';
import TeamMemberService from '../../services/teamMemberService';
import usePermissions from '../../hooks/usePermissions';
import usePayrollReferencePeriod, { PAYROLL_PERIOD_PRESETS } from '../../hooks/usePayrollReferencePeriod';
import { Permissions } from '../../constants/Permissions';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getPaymentModeKey, getPayCadenceKey } from '../../constants/EmployeePaymentModes';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import SummaryStatCard, { SummaryStatGrid } from '../../components/common/SummaryStatCard';
import PayrollProfitSummary from '../../components/employee-payments/PayrollProfitSummary';
import PayrollCashFlowDonut from '../../components/employee-payments/PayrollCashFlowDonut';
import ExpenseService from '../../services/expenseService';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL } from '../../constants/MembershipRevenueDistributionModel';
import { BRAND } from '../../theme/brandTokens';

const ALL_TEAM_MEMBERS_VALUE = '__all__';

const PERIOD_PRESET_OPTIONS = [
  PAYROLL_PERIOD_PRESETS.NEXT_MONTH,
  PAYROLL_PERIOD_PRESETS.TODAY,
  PAYROLL_PERIOD_PRESETS.THIS_WEEK,
  PAYROLL_PERIOD_PRESETS.LAST_WEEK,
  PAYROLL_PERIOD_PRESETS.THIS_MONTH,
  PAYROLL_PERIOD_PRESETS.LAST_MONTH,
  PAYROLL_PERIOD_PRESETS.LAST_3_MONTHS,
  PAYROLL_PERIOD_PRESETS.THIS_YEAR,
  PAYROLL_PERIOD_PRESETS.CUSTOM,
];

const formatReferenceMonth = (year, month, t) => {
  if (!year || !month) return '—';
  const monthLabel = t(`employeePayments.payroll.monthNames.${month}`) || String(month);
  return `${monthLabel} ${year}`;
};

const buildAppliedFilterSummary = (appliedFilters, teamMembers, t) => {
  const periodLabel = appliedFilters.periodPreset === PAYROLL_PERIOD_PRESETS.CUSTOM
    ? t('employeePayments.payroll.filters.customRangeSummary', {
      from: formatUtcDate(`${appliedFilters.dateRange.from}T00:00:00Z`),
      to: formatUtcDate(`${appliedFilters.dateRange.to}T00:00:00Z`),
    })
    : t(`employeePayments.payroll.periodPresets.${appliedFilters.periodPreset}`);

  let membersLabel = t('common.all');
  if (appliedFilters.teamMemberIds?.length) {
    const names = appliedFilters.teamMemberIds
      .map((id) => teamMembers.find((member) => member.id === id)?.name)
      .filter(Boolean);
    membersLabel = names.length ? names.join(', ') : t('common.all');
  }

  return t('employeePayments.payroll.filters.summary', { period: periodLabel, members: membersLabel });
};

const EmployeePayrollDashboardPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const { managementUser } = useAuth();
  const { currentStore } = useAppData();
  const { hasPermission, hasAnyPermission, loading: permissionsLoading } = usePermissions(storeId);

  const {
    periodPreset,
    customFrom,
    customTo,
    setCustomFrom,
    setCustomTo,
    dateRange,
    isRangeValid,
    rangeErrorKey,
    visibleRangeErrorKey,
    touchCustomRange,
    handlePresetChange,
  } = usePayrollReferencePeriod();

  const [reportLoading, setReportLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [serviceReport, setServiceReport] = useState(null);
  const [membershipReport, setMembershipReport] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filterSubmitAttempted, setFilterSubmitAttempted] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [detailsMember, setDetailsMember] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [membershipPots, setMembershipPots] = useState([]);
  const [membershipPeriodPayouts, setMembershipPeriodPayouts] = useState(null);
  const [membershipDistributionModel, setMembershipDistributionModel] = useState(
    MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.MonthlyPot,
  );
  const [cashFlowReport, setCashFlowReport] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const teamMembersLoadedStoreRef = useRef(null);

  const currencyCode = currentStore?.currency || DEFAULT_CURRENCY;
  const canViewStoreReport = hasPermission(Permissions.EmployeePaymentsRead);
  const canAccess = hasAnyPermission([Permissions.EmployeePaymentsRead, Permissions.EmployeePaymentsReadOwn]);
  const canEditCompensation = hasPermission(Permissions.EmployeePaymentsSettingsUpdate);
  const canReadExpenses = hasPermission(Permissions.ExpensesRead);
  const ownTeamMemberId = managementUser?.id || '';

  const teamMemberNames = useMemo(() => {
    const nameMap = {};
    (teamMembers || []).forEach((member) => {
      nameMap[member.id] = member.name;
    });
    return nameMap;
  }, [teamMembers]);

  const allTeamMembersSelected = useMemo(
    () => teamMembers.length > 0 && selectedTeamMemberIds.length === teamMembers.length,
    [selectedTeamMemberIds.length, teamMembers.length],
  );

  const selectedTeamMembers = useMemo(
    () => teamMembers.filter((member) => selectedTeamMemberIds.includes(member.id)),
    [selectedTeamMemberIds, teamMembers],
  );

  const filterRangeErrorKey = filterSubmitAttempted || visibleRangeErrorKey
    ? (visibleRangeErrorKey || rangeErrorKey)
    : null;

  const appliedFilterSummary = useMemo(() => {
    if (!appliedFilters) return '';
    return buildAppliedFilterSummary(appliedFilters, teamMembers, t);
  }, [appliedFilters, teamMembers, t]);

  const loadReport = useCallback(async (filters) => {
    if (!storeId || !canAccess || !filters?.dateRange) {
      return;
    }

    const filterKey = filters.teamMemberIds?.join(',') || 'all';
    const requestKey = `employee-payroll-${storeId}-${filters.dateRange.from}-${filters.dateRange.to}-${filterKey}-${canViewStoreReport ? 'store' : ownTeamMemberId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setReportLoading(true);
        setLoadError(null);

        let serviceData;
        let membershipData;
        let cashFlowData = null;

        if (canViewStoreReport) {
          [serviceData, membershipData, cashFlowData] = await Promise.all([
            employeePaymentService.getStoreServicePayrollReport(
              storeId,
              filters.dateRange,
              filters.teamMemberIds,
            ),
            employeePaymentService.getStoreMembershipPayrollReport(
              storeId,
              filters.dateRange,
              filters.teamMemberIds,
            ),
            employeePaymentService.getStoreCashFlowReport(storeId, filters.dateRange),
          ]);
        } else {
          [serviceData, membershipData] = await Promise.all([
            employeePaymentService.getTeamMemberServicePayrollReport(
              storeId,
              ownTeamMemberId,
              filters.dateRange,
            ),
            employeePaymentService.getTeamMemberMembershipPayrollReport(
              storeId,
              ownTeamMemberId,
              filters.dateRange,
            ),
          ]);
        }

        setServiceReport(serviceData);
        setMembershipReport(membershipData);
        setCashFlowReport(canViewStoreReport ? cashFlowData : null);
        if (canReadExpenses) {
          try {
            const summary = await ExpenseService.getSummary(storeId, filters.dateRange);
            setExpenseSummary(summary);
          } catch (expenseError) {
            console.error('Failed to load expense summary for payroll:', expenseError);
            setExpenseSummary(null);
          }
        } else {
          setExpenseSummary(null);
        }
        setExpandedMemberId(null);

        if (canViewStoreReport) {
          const settings = await employeePaymentService.getMembershipRevenueSettings(storeId);
          const model = settings.distributionModel || MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.MonthlyPot;
          setMembershipDistributionModel(model);

          if (model === MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.PeriodUsageProportional) {
            try {
              const detail = await employeePaymentService.getMembershipPeriodUsagePayoutDetail(
                storeId,
                filters.dateRange,
              );
              setMembershipPeriodPayouts(detail);
            } catch (periodError) {
              console.error('Failed to load membership period payouts:', periodError);
              setMembershipPeriodPayouts({ error: true });
            }
            setMembershipPots([]);
          } else {
            const periods = employeePaymentService.collectMembershipPotPeriods(membershipData);
            const pots = await Promise.all(periods.map(async (period) => {
              try {
                const detail = await employeePaymentService.getMembershipMonthlyPotDetail(
                  storeId,
                  period.year,
                  period.month,
                );
                return { ...period, detail, error: false };
              } catch (potError) {
                console.error('Failed to load membership monthly pot detail:', potError);
                return { ...period, detail: null, error: true };
              }
            }));
            setMembershipPots(pots);
            setMembershipPeriodPayouts(null);
          }
        } else {
          setMembershipPots([]);
          setMembershipPeriodPayouts(null);
        }
      } catch (error) {
        console.error('Failed to load payroll report:', error);
        setLoadError(t('employeePayments.payroll.error.loading'));
        setServiceReport(null);
        setMembershipReport(null);
        setCashFlowReport(null);
        setExpenseSummary(null);
        setMembershipPots([]);
        setMembershipPeriodPayouts(null);
      } finally {
        setReportLoading(false);
      }
    });
  }, [canAccess, canReadExpenses, canViewStoreReport, ownTeamMemberId, storeId, t]);

  useEffect(() => {
    if (permissionsLoading || !storeId || !canViewStoreReport) {
      return;
    }
    if (teamMembersLoadedStoreRef.current === storeId) {
      return;
    }

    requestDeduplicator
      .deduplicate(`payroll-team-members-${storeId}`, () => TeamMemberService.getTeamMembersByStore(storeId))
      .then((members) => {
        teamMembersLoadedStoreRef.current = storeId;
        const activeMembers = (members || []).filter((member) => !member.disabled);
        setTeamMembers(activeMembers);
        setSelectedTeamMemberIds(activeMembers.map((member) => member.id));
      })
      .catch((error) => {
        console.error('Failed to load team members for payroll filter:', error);
      });
  }, [permissionsLoading, storeId, canViewStoreReport]);

  useEffect(() => {
    if (!appliedFilters) {
      return;
    }
    loadReport(appliedFilters);
  }, [appliedFilters, loadReport]);

  const handleTeamMemberSelection = (event) => {
    const { value } = event.target;
    const values = typeof value === 'string' ? value.split(',') : value;

    if (values.includes(ALL_TEAM_MEMBERS_VALUE)) {
      if (allTeamMembersSelected) {
        setSelectedTeamMemberIds([]);
      } else {
        setSelectedTeamMemberIds(teamMembers.map((member) => member.id));
      }
      return;
    }

    setSelectedTeamMemberIds(values);
  };

  const handleViewReport = () => {
    setFilterSubmitAttempted(true);

    if (periodPreset === PAYROLL_PERIOD_PRESETS.CUSTOM) {
      touchCustomRange();
      if (rangeErrorKey) {
        return;
      }
    }

    if (!isRangeValid || !dateRange) {
      return;
    }

    const teamMemberIds = canViewStoreReport
      ? (
        selectedTeamMemberIds.length === 0 || selectedTeamMemberIds.length === teamMembers.length
          ? null
          : [...selectedTeamMemberIds]
      )
      : null;

    setAppliedFilters({
      periodPreset,
      dateRange,
      teamMemberIds,
    });
    setFiltersExpanded(false);
  };

  const handleReloadReport = () => {
    if (appliedFilters) {
      loadReport(appliedFilters);
    }
  };

  if (permissionsLoading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('employeePayments.payroll.title')}
        pageSubtitle={t('employeePayments.payroll.subtitle')}
        variant="cards"
      />
    );
  }

  return (
    <ManagementPageLayout
      pageTitle={t('employeePayments.payroll.title')}
      pageSubtitle={canViewStoreReport ? t('employeePayments.payroll.subtitle') : t('employeePayments.payroll.subtitleOwn')}
      contentSx={{ maxWidth: '1200px', width: '100%' }}
    >
      <ManagementPageCard>
        <Stack spacing={3} sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Box
            sx={{
              border: `1px solid ${BRAND.line}`,
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: BRAND.surface,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: BRAND.background,
                borderBottom: filtersExpanded ? `1px solid ${BRAND.line}` : 'none',
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
                  {t('employeePayments.payroll.filters.title')}
                </Typography>
                {!filtersExpanded && appliedFilters && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {appliedFilterSummary}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {appliedFilters && !filtersExpanded && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterSubmitAttempted(false);
                      setFiltersExpanded(true);
                    }}
                    aria-label={t('employeePayments.payroll.filters.edit')}
                    sx={{ color: BRAND.accentText }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => setFiltersExpanded((expanded) => !expanded)}
                  aria-label={filtersExpanded
                    ? t('employeePayments.payroll.filters.collapse')
                    : t('employeePayments.payroll.filters.expand')}
                >
                  {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Stack>

            <Collapse in={filtersExpanded}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ md: periodPreset === PAYROLL_PERIOD_PRESETS.CUSTOM ? 'flex-start' : 'flex-end' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ md: periodPreset === PAYROLL_PERIOD_PRESETS.CUSTOM ? 'flex-start' : 'flex-end' }}
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      flex: { md: '0 1 auto', lg: '0 1 auto' },
                    }}
                  >
                    <SalovareSelectField
                      label={t('employeePayments.payroll.referencePeriod')}
                      value={periodPreset}
                      onChange={(event) => handlePresetChange(event.target.value)}
                      sx={{ width: '100%', maxWidth: { xs: '100%', md: 240 }, flex: { md: '0 1 240px' } }}
                    >
                      {PERIOD_PRESET_OPTIONS.map((preset) => (
                        <MenuItem key={preset} value={preset}>
                          {t(`employeePayments.payroll.periodPresets.${preset}`)}
                        </MenuItem>
                      ))}
                    </SalovareSelectField>

                    {periodPreset === PAYROLL_PERIOD_PRESETS.CUSTOM && (
                      <SalovareDateRangePicker
                        startValue={customFrom}
                        endValue={customTo}
                        onStartChange={setCustomFrom}
                        onEndChange={setCustomTo}
                        onStartBlur={touchCustomRange}
                        onEndBlur={touchCustomRange}
                        error={Boolean(filterRangeErrorKey)}
                        helperText={filterRangeErrorKey ? t(filterRangeErrorKey) : undefined}
                        disableFuture
                        sx={{
                          width: '100%',
                          maxWidth: { xs: '100%', md: 400 },
                          flex: { md: '0 1 400px' },
                        }}
                      />
                    )}
                  </Stack>

                  {canViewStoreReport && (
                    <SalovareSelectField
                      label={t('employeePayments.payroll.teamMemberFilter')}
                      multiple
                      displayEmpty
                      value={selectedTeamMemberIds}
                      onChange={handleTeamMemberSelection}
                      sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', md: 240 },
                        flex: { md: '1 1 280px', lg: '1 1 280px' },
                      }}
                      renderValue={(selected) => {
                        if (!selected?.length) {
                          return (
                            <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                              {t('employeePayments.payroll.teamMemberFilterPlaceholder')}
                            </Typography>
                          );
                        }
                        if (allTeamMembersSelected) return t('common.all');
                        return selectedTeamMembers.map((member) => member.name).join(', ');
                      }}
                    >
                      <MenuItem value={ALL_TEAM_MEMBERS_VALUE}>
                        <Checkbox checked={allTeamMembersSelected} />
                        <ListItemText primary={t('common.all')} />
                      </MenuItem>
                      {teamMembers.map((teamMember) => (
                        <MenuItem key={teamMember.id} value={teamMember.id}>
                          <Checkbox checked={selectedTeamMemberIds.includes(teamMember.id)} />
                          <ListItemText primary={teamMember.name} />
                        </MenuItem>
                      ))}
                    </SalovareSelectField>
                  )}
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                  <PrimaryActionButton onClick={handleViewReport}>
                    {t('employeePayments.payroll.filters.view')}
                  </PrimaryActionButton>
                </Stack>
              </Stack>
            </Collapse>
          </Box>

          {loadError && <Alert severity="error">{loadError}</Alert>}

          {appliedFilters?.periodPreset === PAYROLL_PERIOD_PRESETS.NEXT_MONTH && !reportLoading && (
            <Alert severity="info">{t('employeePayments.payroll.estimatedPeriodAlert')}</Alert>
          )}

          {appliedFilters && reportLoading && (
            <SalovareLoader size="medium" center sx={{ py: 4 }} />
          )}

          {appliedFilters && !reportLoading && canViewStoreReport && canReadExpenses && (
            <PayrollProfitSummary
              visible={!appliedFilters.teamMemberIds?.length && Boolean(expenseSummary) && Boolean(serviceReport)}
              filtered={Boolean(appliedFilters.teamMemberIds?.length)}
              storeGain={serviceReport?.storeTotals?.storeGain}
              membershipMarginRetained={membershipReport?.storeTotals?.storeMarginRetained}
              postedExpenses={expenseSummary?.postedTotal}
              currencyCode={currencyCode}
            />
          )}

          {appliedFilters && !reportLoading && cashFlowReport && canViewStoreReport && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 1 }}>
                {t('employeePayments.payroll.sections.cashFlow')}
              </Typography>
              <PayrollCashFlowDonut report={cashFlowReport} currencyCode={currencyCode} />
            </Box>
          )}

          {appliedFilters && !reportLoading && serviceReport && canViewStoreReport && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 1 }}>
                {t('employeePayments.payroll.sections.service')}
              </Typography>
              <SummaryStatGrid sx={{ mb: 2 }}>
              <SummaryStatCard
                label={t('employeePayments.payroll.totals.grossBilled')}
                value={serviceReport.storeTotals.grossBilled}
                currencyCode={currencyCode}
                tone="keep"
              />
              <SummaryStatCard
                label={t('employeePayments.payroll.totals.productsGrossSold')}
                value={serviceReport.storeTotals.productsGrossSold}
                currencyCode={currencyCode}
                tone="keep"
              />
              <SummaryStatCard
                label={t('employeePayments.payroll.totals.productCommissionsPaid')}
                value={serviceReport.storeTotals.productCommissionsPaid}
                currencyCode={currencyCode}
                tone="cost"
              />
              <SummaryStatCard
                label={t('employeePayments.payroll.totals.salaries')}
                value={serviceReport.storeTotals.totalPayout}
                currencyCode={currencyCode}
                tone="cost"
              />
              <SummaryStatCard
                label={t('employeePayments.payroll.totals.storeGain')}
                value={serviceReport.storeTotals.storeGain}
                currencyCode={currencyCode}
                emphasizeNegative
                tone="keep"
              />
            </SummaryStatGrid>
            </Box>
          )}

          {appliedFilters && !reportLoading && !serviceReport?.members?.length ? (
            <Alert severity="info">{t('employeePayments.payroll.emptyService')}</Alert>
          ) : appliedFilters && !reportLoading && serviceReport?.members?.length ? (
            <TableContainer sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: BRAND.background }}>
                    {canViewStoreReport && <TableCell>{t('employeePayments.payroll.table.member')}</TableCell>}
                    <TableCell>{t('employeePayments.payroll.table.mode')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.commissionRate')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.fixedSalary')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.commission')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.productsSold')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.productCommission')}</TableCell>
                    <TableCell align="right">{t('employeePayments.payroll.table.total')}</TableCell>
                    <TableCell align="center">{t('employeePayments.payroll.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceReport.members.map((member) => {
                    const isExpanded = expandedMemberId === member.teamMemberId;
                    const modeKey = getPaymentModeKey(member.paymentMode);

                    return (
                      <React.Fragment key={member.teamMemberId}>
                        <TableRow hover>
                          {canViewStoreReport && (
                            <TableCell sx={{ fontWeight: 600 }}>
                              {teamMemberNames[member.teamMemberId] || t('employeePayments.payroll.unknownMember')}
                            </TableCell>
                          )}
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="body2">
                                {t(`employeePayments.compensation.modes.${modeKey}`)}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(`employeePayments.compensation.payCadenceOptions.${getPayCadenceKey(member.payCadence)}`)}
                              />
                              {member.minimumApplied && (
                                <Chip size="small" label={t('employeePayments.payroll.minimumApplied')} color="warning" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {member.commissionRatePercent != null
                              ? `${member.commissionRatePercent}%`
                              : t('common.notProvided')}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(member.fixedSalary, currencyCode)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(member.commissionAmount, currencyCode)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(member.productsGrossSold, currencyCode)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(member.productCommissionAmount, currencyCode)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(member.totalPayout, currencyCode)}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDetailsMember({
                                    id: member.teamMemberId,
                                    name: teamMemberNames[member.teamMemberId],
                                  });
                                  setDetailsDialogOpen(true);
                                }}
                                aria-label={t('employeePayments.payroll.table.viewAppointments')}
                                sx={{ color: BRAND.textMuted }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {canEditCompensation && (
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingMember({
                                    id: member.teamMemberId,
                                    name: teamMemberNames[member.teamMemberId],
                                  })}
                                  aria-label={t('employeePayments.payroll.table.editCompensation')}
                                  sx={{ color: BRAND.accentText }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => setExpandedMemberId(isExpanded ? null : member.teamMemberId)}
                                aria-label={t('employeePayments.payroll.table.details')}
                              >
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={canViewStoreReport ? 9 : 8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2 }}>
                                {!member.breakdown?.length ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {t('employeePayments.payroll.noBreakdown')}
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>{t('employeePayments.payroll.breakdown.source')}</TableCell>
                                        <TableCell>{t('employeePayments.payroll.breakdown.recognitionDate')}</TableCell>
                                        <TableCell align="right">{t('employeePayments.payroll.breakdown.serviceRevenue')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {member.breakdown.map((line) => {
                                        const isAdditionalCost = isAppointmentAdditionalCostSource(line.sourceType);
                                        return (
                                          <TableRow key={line.id}>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {t(`employeePayments.payroll.sourceTypes.${getPayrollSourceTypeKey(line.sourceType)}`)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              {line.recognitionDate
                                                ? formatUtcDate(`${line.recognitionDate}T00:00:00Z`)
                                                : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                              {isAdditionalCost ? (
                                                <Stack spacing={0.25} alignItems="flex-end">
                                                  <Typography variant="body2">
                                                    {t('employeePayments.payroll.breakdown.extraBilled')}: {formatCurrency(line.grossAmount, currencyCode)}
                                                  </Typography>
                                                  <Typography variant="body2" color="text.secondary">
                                                    {t('employeePayments.payroll.breakdown.employeeShare')}: {formatCurrency(line.netCommissionBaseAmount, currencyCode)}
                                                  </Typography>
                                                </Stack>
                                              ) : (
                                                formatCurrency(line.grossAmount, currencyCode)
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {appliedFilters && !reportLoading && membershipReport && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 2 }}>
                {t('employeePayments.payroll.sections.membership')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('employeePayments.payroll.sections.membershipHint')}
              </Typography>

              {canViewStoreReport && membershipDistributionModel === MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.PeriodUsageProportional ? (
                membershipPeriodPayouts?.error ? (
                  <Alert severity="error">{t('employeePayments.payroll.membershipPeriod.error.loading')}</Alert>
                ) : membershipPeriodPayouts?.periods?.length ? (
                  <MembershipPeriodUsagePanel
                    detail={membershipPeriodPayouts}
                    currencyCode={currencyCode}
                  />
                ) : (
                  <Alert severity="info">{t('employeePayments.payroll.emptyMembership')}</Alert>
                )
              ) : !membershipReport.members?.length ? (
                <Alert severity="info">{t('employeePayments.payroll.emptyMembership')}</Alert>
              ) : canViewStoreReport ? (
                <Stack spacing={4}>
                  {membershipPots.map((pot) => (
                    <Box key={`${pot.year}-${pot.month}`}>
                      {pot.error && (
                        <Alert severity="error">{t('employeePayments.payroll.membershipPot.error.loading')}</Alert>
                      )}
                      {!pot.error && pot.detail && (
                        <MembershipMonthlyPotPanel
                          detail={pot.detail}
                          recognitionDate={pot.recognitionDate}
                          estimated={pot.estimated}
                          currencyCode={currencyCode}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: BRAND.background }}>
                        <TableCell>{t('employeePayments.payroll.membershipTable.referenceMonth')}</TableCell>
                        <TableCell>{t('employeePayments.payroll.breakdown.recognitionDate')}</TableCell>
                        <TableCell align="right">{t('employeePayments.payroll.membershipTable.shareAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {membershipReport.members.flatMap((member) => (
                        member.breakdown.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>
                              {formatReferenceMonth(
                                line.referenceCalendarYear,
                                line.referenceCalendarMonth,
                                t,
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <span>
                                  {line.recognitionDate
                                    ? formatUtcDate(`${line.recognitionDate}T00:00:00Z`)
                                    : '—'}
                                </span>
                                {line.status === 'provisional' && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label={t('employeePayments.payroll.estimated')}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(line.shareAmount, currencyCode)}
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Stack>
      </ManagementPageCard>

      <TeamMemberCompensationDialog
        open={Boolean(editingMember)}
        onClose={() => setEditingMember(null)}
        storeId={storeId}
        teamMemberId={editingMember?.id}
        teamMemberName={editingMember?.name}
        canEdit={canEditCompensation}
        onSaved={handleReloadReport}
      />

      {detailsMember && (
        <EmployeePayrollAppointmentDetailsDialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          onExited={() => setDetailsMember(null)}
          storeId={storeId}
          teamMemberId={detailsMember.id}
          teamMemberName={detailsMember.name}
          dateRange={appliedFilters?.dateRange}
          currencyCode={currencyCode}
        />
      )}
    </ManagementPageLayout>
  );
};

export default EmployeePayrollDashboardPage;
